import type { Analysis, AnalysisBrief, OverviewResponse } from '@bsod/contracts';
import { api } from './client';

export type EventStatus = 'pending' | 'processing' | 'success' | 'failed';

export interface StatusFrame {
  id: string;
  status: EventStatus;
  phase: string | null;
  phase_started_at: string | null;
}

export function parseStatusFrame(data: string): StatusFrame | null {
  try {
    const raw = JSON.parse(data) as {
      id: string;
      status: EventStatus;
      phase?: string | null;
      phase_started_at?: string | null;
    };
    return {
      id: raw.id,
      status: raw.status,
      phase: raw.phase ?? null,
      phase_started_at: raw.phase_started_at ?? null,
    };
  } catch {
    return null;
  }
}

export interface BsodEvent {
  id: string;
  public_id: string;
  project_id: string;
  project_public_id: string;
  device_id: string | null;
  case_id: string | null;
  case_public_id: string | null;
  case_title: string | null;
  uploaded_at: string;
  created_at: string;
  updated_at: string;
  uploaded_by_user_id: string | null;
  uploader_email: string | null;
  uploader_display_name: string | null;
  status: EventStatus;
  tags: string[];
  description: string | null;
  bugcheck_code: string | null;
  bugcheck_name: string | null;
  bucket_id: string | null;
  /**
   * Global number of projects containing this bucket signature; 0 when the
   * event has no bucket. The list uses 2+ as the common-defect threshold.
   */
  common_defect_project_count: number;
  failure_bucket_id: string | null;
  faulting_module: string | null;
  bios_vendor: string | null;
  bios_version: string | null;
  // Round-3 LLM-generated short fields. Picked by locale on the frontend.
  title_en: string | null;
  title_zh: string | null;
  summary_en: string | null;
  summary_zh: string | null;
}

/** Facts projected onto list rows; detail requests do not repeat them at top level. */
type ListProjectedEventFacts = Pick<
  BsodEvent,
  | 'case_title'
  | 'bugcheck_code'
  | 'bugcheck_name'
  | 'bucket_id'
  | 'common_defect_project_count'
  | 'failure_bucket_id'
  | 'faulting_module'
  | 'bios_vendor'
  | 'bios_version'
  | 'title_en'
  | 'title_zh'
  | 'summary_en'
  | 'summary_zh'
>;

/**
 * Detail shape returned by `GET /events/:publicId`.
 *
 * Crash / machine / stack / module facts live under `analysis.*` (single
 * source of truth). The list-only fields remain optional because a detail
 * response is sometimes combined with a cached list row by callers; they are
 * never guaranteed on the endpoint itself.
 */
export type EventDetail = Omit<BsodEvent, keyof ListProjectedEventFacts> &
  Partial<ListProjectedEventFacts> & {
    oss_object_key: string;
    oss_size_bytes: number;
    oss_sha256: string | null;
    current_analysis_run_id: string | null;
    analysis: Analysis | null;
    analyses: AnalysisBrief[];
    analysis_phase?: string | null;
    phase_started_at?: string | null;
  };

export interface EventsListResponse {
  items: BsodEvent[];
  next_cursor: string | null;
  /**
   * How many events sit in each status under every *other* filter (the scope,
   * the search, the device) — the counts the status facet prints. The status
   * filter itself is left out, and a status with no rows is absent.
   */
  status_counts?: Partial<Record<EventStatus, number>>;
}

/** Mirrors `RecentEventCountResponse` in `@bsod/contracts`. */
export interface RecentEventCount {
  count: number;
  hours: number;
}

export type DumpKind = 'full' | 'minidump';

export interface PrimaryDumpInput {
  oss_object_key: string;
  filename: string;
  size_bytes: number;
  sha256: string;
  dump_kind: DumpKind;
  dump_file_mtime?: string | null;
  collected_at?: string | null;
  trigger_reason?: string | null;
  client_version?: string | null;
  /** @deprecated pre-rename spelling, still accepted by the intake */
  cli_version?: string | null;
}

export interface AttachmentInput {
  oss_object_key: string;
  filename: string;
  size_bytes: number;
  sha256: string;
}

export interface DeviceSourceInfo {
  hostname: string;
  mac_addresses?: string[];
  smbios_uuid?: string | null;
  smbios_serial?: string | null;
  last_boot_time?: string | null;
}

export interface IntakeRequestDto {
  project_id: string;
  uploaded_via: 'engineer' | 'relay';
  uploaded_via_relay_id?: string | null;
  uploaded_via_batch_token_jti?: string | null;
  device: DeviceSourceInfo;
  primary_dump: PrimaryDumpInput;
  attachments?: AttachmentInput[];
  description?: string | null;
}

export type IntakeResponse = import('@bsod/contracts').IntakeResponse;

export const eventsApi = {
  list: (
    params: {
      project?: string;
      /** One status, or several — they OR together. */
      status?: EventStatus | EventStatus[];
      cursor?: string;
      limit?: number;
      q?: string;
    } = {},
  ) => {
    const search = new URLSearchParams();
    if (params.project) search.set('project', params.project);
    // Repeated, not comma-joined: `?status=failed&status=pending` is what the
    // contract's union accepts, and a status value never contains a comma.
    for (const status of params.status ? [params.status].flat() : []) {
      search.append('status', status);
    }
    if (params.cursor) search.set('cursor', params.cursor);
    if (params.limit) search.set('limit', String(params.limit));
    if (params.q) search.set('q', params.q);
    return api.get<EventsListResponse>(`/events?${search.toString()}`).then((r) => r.data);
  },

  get: (publicId: string, lang?: 'en' | 'zh', runId?: string | null) => {
    const qs = new URLSearchParams();
    if (lang) qs.set('lang', lang);
    if (runId) qs.set('run', runId);
    const tail = qs.toString();
    return api.get<EventDetail>(`/events/${publicId}${tail ? `?${tail}` : ''}`).then((r) => r.data);
  },

  intake: (dto: IntakeRequestDto) =>
    api.post<IntakeResponse>('/events/intake', dto).then((r) => r.data),

  updateTags: (publicId: string, tags: string[]) =>
    api.patch(`/events/${publicId}`, { tags }).then((r) => r.data),

  /**
   * PA+ — the description's own route, a rung below the lead+ tags route, so a
   * PA can annotate an event without the tag/lifecycle writes that come with it.
   */
  updateDescription: (publicId: string, description: string | null) =>
    api.patch(`/events/${publicId}/description`, { description }).then((r) => r.data),

  reanalyze: (publicId: string) => api.post(`/events/${publicId}/reanalyze`).then((r) => r.data),

  remove: (publicId: string) => api.delete(`/events/${publicId}`).then(() => undefined),

  /**
   * Uploads in a rolling window, across every project the caller can see — one
   * count for the rail badge, instead of one dashboard request per project.
   */
  recentCount: (hours = 24) =>
    api.get<RecentEventCount>(`/events/recent-count?hours=${hours}`).then((r) => r.data),

  /**
   * The landing page's numbers, aggregated over every project the caller can
   * see — one request where a per-project dashboard would be one per project.
   */
  overview: (days: 7 | 30 | 90) =>
    api.get<OverviewResponse>(`/events/overview?days=${days}`).then((r) => r.data),

  streamUrl: (publicId: string, accessToken: string) =>
    `/api/v1/events/${publicId}/stream?access_token=${encodeURIComponent(accessToken)}`,
};
